import { memo, useCallback, useEffect, useRef } from 'react';
import useAddMenuItems from './useAddMenuItems';
import { AddMenuProps } from './types';
import { ADD_MENU_POPOVER_TEST_ID } from './constants';
import Icon from '@/components/atoms/Icon';
import TooltipV2 from '@/components/TooltipV2';

const AddMenuPopover = ({ isOpen, onClose }: Omit<AddMenuProps, 'items'>) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleItemClick = useCallback(
    async (onClick: () => Promise<void> | void) => {
      await onClick();
      onClose();
    },
    [onClose]
  );

  const { groups } = useAddMenuItems();

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={popoverRef}
      data-testid={ADD_MENU_POPOVER_TEST_ID}
      className="absolute bottom-12 left-0 z-50 min-w-[180px] rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
      role="menu"
      aria-label="Add menu"
    >
      {groups.map((group, groupIndex) => (
        <div key={group.name}>
          {group.items.map(item => (
            <button
              key={item.id}
              data-testid={`add-menu-item-${item.id}`}
              className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-white"
              onClick={() => {
                if (item.onClick) {
                  handleItemClick(item.onClick);
                }
              }}
              disabled={item.isDisabled}
              role="menuitem"
              tabIndex={0}
            >
              <Icon name={item.iconName as any} size={16} stroke={2} />
              <TooltipV2
                tooltipId={`add-menu-item-${item.id}-tooltip`}
                tooltipContent={item.reasonForDisabled || ''}
                place="top"
                tooltipElement={<span className="flex-1">{item.label}</span>}
              />
              {item.showBetaTag && (
                <span className="rounded-xl bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Beta</span>
              )}
            </button>
          ))}
          {groupIndex < groups.length - 1 && (
            <div className="my-1 border-t border-gray-200" data-testid="group-divider" />
          )}
        </div>
      ))}
    </div>
  );
};

export default memo(AddMenuPopover);
